/**
 * ReferenceTrackContext
 * Shared reference track analysis state across MidiForge and Production Intelligence.
 * Both tabs consume the same upload/analysis state via this context.
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { useReferenceTrack } from '@hooks/useReferenceTrack';
import type { ReferenceTrackAnalysis, AnalysisStatus } from '@/types/referenceTrack.types';

interface ReferenceTrackContextValue {
  analysis: ReferenceTrackAnalysis | null;
  status: AnalysisStatus;
  error: string | null;
  uploadAndAnalyze: (file: File) => Promise<void>;
  analyzeWithoutUpload: (file: File) => Promise<void>;
  clear: () => void;
}

const ReferenceTrackContext = createContext<ReferenceTrackContextValue | null>(null);

export const ReferenceTrackProvider: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const refTrack = useReferenceTrack();

  return (
    <ReferenceTrackContext.Provider value={refTrack}>
      {children}
    </ReferenceTrackContext.Provider>
  );
};

export function useSharedReferenceTrack(): ReferenceTrackContextValue {
  const ctx = useContext(ReferenceTrackContext);
  if (!ctx) {
    throw new Error('useSharedReferenceTrack must be used within a ReferenceTrackProvider');
  }
  return ctx;
}
